'use client';

import { Modal, PageLayoutV2 as UikitPageLayout } from '@sgcmpteltd/uikit-next';
import {
  LINK_KEY_REPORT_ERROR,
} from '@sgcmpteltd/uikit-next/components/page-footer-v2/mobile';
import {
  LINK_KEY_NEW_CARS,
} from '@sgcmpteltd/uikit-next/components/page-header/desktop';
import {
  HEADER_LAYOUT_APP,
  HEADER_LAYOUT_SITE,
} from '@sgcmpteltd/uikit-next/components/page-header/mobile';
import {
  HEADER_TITLE_TEXT,
} from '@sgcmpteltd/uikit-next/components/page-header/mobile-v2';
import { LoadingSpinner } from '@sgcmpteltd/uikit-react';
import {
  Result,
  RESULT_VARIANT_ERROR,
  RESULT_VARIANT_SUCCESS,
} from '@sgcmpteltd/uikit-react/components/result';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useLayoutEffect, useState } from 'react';

import { ACTION_STATUS_SUCCESS } from '@/actions';
import {
  clearViewDesktopCookie,
} from '@/actions/misc/home/clear-view-desktop-cookie';
import {
  setViewDesktopCookie,
} from '@/actions/misc/home/set-view-desktop-cookie';
import { reportError } from '@/actions/misc/submit-report-error-message';
import type {
  ReportErrorResult,
} from '@/app/(routes)/(misc)/pricelists/_components/page-content/pi-pricelist-content/types'; // @suppress longLineCheck
import { AppContext } from '@/context/app';
import { UserContext } from '@/context/user';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';
import { useShoppingCartCount } from '@/hooks/use-shopping-cart-count';

import layoutStyles from '../../styles.module.scss';
import { pageRouteConfigMap } from './configs';
import {
  HASH_ADVANCED_FILTER_POPUP_NAME,
  PAGE_NOT_FOUND_TITLE,
  PROCESS_RESULT_MESSAGE_REPORT_ERROR_FAIL,
  PROCESS_RESULT_MESSAGE_REPORT_ERROR_SUCCESS,
  PROCESS_RESULT_TITLE_REPORT_ERROR_FAIL,
  PROCESS_RESULT_TITLE_REPORT_ERROR_SUCCESS,
} from './constants';
import { PtoAds } from './pto-ads';
import styles from './styles.module.scss';
import type { Component } from './types';
import { isPageRoute, pathnameToPageRoute } from './utils';

export const PageLayout: Component = ({
  children,
  isApp,
  userId,
  username,
  allowViewDesktop,
  allowViewMobile,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { Provider: AppContextProvider } = AppContext;
  const { Provider: UserContextProvider } = UserContext;
  const { count: shoppingCartCount } = useShoppingCartCount();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [
    reportErrorResultData, setReportErrorResultData,
  ] = useState<ReportErrorResult | null>(null);
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  useEffect(() => {
    if (isFilterModalOpen) {
      window.location.hash = `#${HASH_ADVANCED_FILTER_POPUP_NAME}`;
    }
  }, [isFilterModalOpen]);

  useEffect(() => {
    const toggleFilterModal = () => {
      if (window.location.hash.slice(1) === HASH_ADVANCED_FILTER_POPUP_NAME) {
        setIsFilterModalOpen(true);
      } else {
        setIsFilterModalOpen(false);
      }
    };

    window.addEventListener('hashchange', toggleFilterModal);
  }, []);

  useBodyScrollLock({ isLocked: isFilterModalOpen });
  useLayoutEffect(() => {
    const { classList: bodyClassList } = document.body;
    const { isReady: isBodyReadyClassName } = layoutStyles;

    if (bodyClassList.contains(isBodyReadyClassName)) {
      return;
    }

    bodyClassList.add(isBodyReadyClassName);
  }, []);

  const handleViewDesktopClick: React.MouseEventHandler<
    HTMLAnchorElement
  > = async (e) => {
    e.preventDefault();

    await setViewDesktopCookie();
    document.location.reload();
  };

  const handleViewMobileClick: React.MouseEventHandler<
    HTMLButtonElement
  > = async (e) => {
    e.preventDefault();

    await clearViewDesktopCookie();
    document.location.reload();
  };

  if (!isPageRoute(pathname)) {
    return (
      <>
        <UserContextProvider
          value={
            (userId && username)
              ? {
                userId,
                username,
              }
              : null
          }>
          <UikitPageLayout
            activeLink={LINK_KEY_NEW_CARS}
            allowViewDesktop={allowViewDesktop}
            allowViewMobile={allowViewMobile}
            baseUrlMap={{
              mobile: 'https://m.sgcarmart.com',
              desktop: 'https://sgcarmart.com',
            }}
            layout={
              isApp
                ? HEADER_LAYOUT_APP
                : HEADER_LAYOUT_SITE
            }
            mobileFooterLinkDisableMap={{
              [LINK_KEY_REPORT_ERROR]: true,
            }}
            shoppingCartCounterValue={shoppingCartCount}
            title={{ type: HEADER_TITLE_TEXT, content: PAGE_NOT_FOUND_TITLE }}
            user={
              username
                ? {
                  username,
                }
                : undefined
            }
            onGoBack={() => router.replace('/')}
            onReportSubmit={async ({ name, email, description }) => {
              const formData = new FormData();

              formData.set('email', email);
              formData.set('description', description);
              formData.set('link', currentUrl.toString());
              formData.set('name', name);

              try {
                setIsSubmitting(true);
                const actionResult = await reportError(formData);

                const isSuccess = actionResult.status === ACTION_STATUS_SUCCESS;
                const resultData = {
                  isSuccess,
                  title: isSuccess
                    ? PROCESS_RESULT_TITLE_REPORT_ERROR_SUCCESS
                    : PROCESS_RESULT_TITLE_REPORT_ERROR_FAIL,
                  message: isSuccess
                    ? PROCESS_RESULT_MESSAGE_REPORT_ERROR_SUCCESS
                    : PROCESS_RESULT_MESSAGE_REPORT_ERROR_FAIL,
                };

                setReportErrorResultData(resultData);
              } catch (error) {
                setReportErrorResultData({
                  isSuccess: false,
                  title: PROCESS_RESULT_TITLE_REPORT_ERROR_FAIL,
                  message: PROCESS_RESULT_MESSAGE_REPORT_ERROR_FAIL,
                });
              } finally {
                setIsSuccessModalVisible(true);
                setIsSubmitting(false);
              }
            }}
            onViewDesktopClick={handleViewDesktopClick}
            onViewMobileClick={handleViewMobileClick}>
            {children}
          </UikitPageLayout>
        </UserContextProvider>
        {isSubmitting && (
          <div className={styles.containerLoadingSpinner}>
            <LoadingSpinner />
          </div>
        )}
        <div id="report-modal">
          <Modal
            className={styles.containerModal}
            isOpen={isSuccessModalVisible}
            title=""
            isBackdropStatic
            onClose={() => setIsSuccessModalVisible(false)}>
            { reportErrorResultData
            && (
              <Result
                message={reportErrorResultData.message}
                title={reportErrorResultData.title}
                variant={
                  reportErrorResultData.isSuccess
                    ? RESULT_VARIANT_SUCCESS
                    : RESULT_VARIANT_ERROR
                } />
            )}
          </Modal>
        </div>
      </>
    );
  }

  const {
    [pathnameToPageRoute(pathname)]: {
      title: pageRouteTitle,
      app: {
        headerContent: pageRouteAppHeaderContent,
        goBackUrl: pageRouteAppGoBackUrl,
      },
      onSubmit: pageRouteOnSubmit,
    },
  } = pageRouteConfigMap(searchParams, {
    isApp,
    isFilterModalOpen,
    setIsFilterModalOpen,
  });

  const handleGoBack = () => {
    if (!pageRouteAppGoBackUrl) {
      return;
    }

    if (typeof pageRouteAppGoBackUrl === 'function') {
      pageRouteAppGoBackUrl();
      return;
    }

    router.replace(pageRouteAppGoBackUrl);
  };

  return (
    <>
      <AppContextProvider
        value={{
          isApp,
          isFilterModalOpen,
          setIsFilterModalOpen,
        }}>
        <UserContextProvider
          value={
            (userId && username)
              ? {
                userId,
                username,
              }
              : null
          }>
          <UikitPageLayout
            activeLink={LINK_KEY_NEW_CARS}
            allowViewDesktop={allowViewDesktop}
            allowViewMobile={allowViewMobile}
            baseUrlMap={{
              mobile: 'https://m.sgcarmart.com',
              desktop: 'https://sgcarmart.com',
            }}
            content={pageRouteAppHeaderContent}
            layout={
              isApp
                ? HEADER_LAYOUT_APP
                : HEADER_LAYOUT_SITE
            }
            mobileFooterLinkDisableMap={{
              [LINK_KEY_REPORT_ERROR]: true,
            }}
            shoppingCartCounterValue={shoppingCartCount}
            title={pageRouteTitle}
            user={
              username
                ? {
                  username,
                }
                : undefined
            }
            onGoBack={handleGoBack}
            onReportSubmit={async ({ name, email, description }) => {
              const formData = new FormData();

              formData.set('email', email);
              formData.set('description', description);
              formData.set('link', currentUrl.toString());
              formData.set('name', name);

              try {
                setIsSubmitting(true);
                const actionResult = await reportError(formData);

                const isSuccess = actionResult.status === ACTION_STATUS_SUCCESS;
                const resultData = {
                  isSuccess,
                  title: isSuccess
                    ? PROCESS_RESULT_TITLE_REPORT_ERROR_SUCCESS
                    : PROCESS_RESULT_TITLE_REPORT_ERROR_FAIL,
                  message: isSuccess
                    ? PROCESS_RESULT_MESSAGE_REPORT_ERROR_SUCCESS
                    : PROCESS_RESULT_MESSAGE_REPORT_ERROR_FAIL,
                };

                setReportErrorResultData(resultData);
              } catch (error) {
                setReportErrorResultData({
                  isSuccess: false,
                  title: PROCESS_RESULT_TITLE_REPORT_ERROR_FAIL,
                  message: PROCESS_RESULT_MESSAGE_REPORT_ERROR_FAIL,
                });
              } finally {
                setIsSuccessModalVisible(true);
                setIsSubmitting(false);
              }
            }}
            onSubmit={pageRouteOnSubmit}
            onViewDesktopClick={handleViewDesktopClick}
            onViewMobileClick={handleViewMobileClick}>
            {children}
            <PtoAds />
          </UikitPageLayout>
        </UserContextProvider>
      </AppContextProvider>
      {isSubmitting && (
        <div className={styles.containerLoadingSpinner}>
          <LoadingSpinner />
        </div>
      )}
      <div id="report-modal">
        <Modal
          className={styles.containerModal}
          isOpen={isSuccessModalVisible}
          title=""
          isBackdropStatic
          onClose={() => setIsSuccessModalVisible(false)}>
          { reportErrorResultData
          && (
            <Result
              className={styles.errorMsg}
              message={reportErrorResultData.message}
              title={reportErrorResultData.title}
              variant={
                reportErrorResultData.isSuccess
                  ? RESULT_VARIANT_SUCCESS
                  : RESULT_VARIANT_ERROR
              } />
          )}
        </Modal>
      </div>
    </>
  );
};
